import React, { useState, useEffect } from 'react';
import { BarChart3, Package, TrendingUp, DollarSign, ArrowUpRight, ArrowDownRight, Bell, LayoutDashboard, UtensilsCrossed, Boxes, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import ProductManager from '../components/ProductManager';
import InventoryManager from '../components/InventoryManager';
import OrderManager from '../components/OrderManager';
import { ChangePasscode } from '../components/ChangePasscode';
import apiClient from '../api/client';
import { BrandLogo } from '../components/BrandLogo';
import { ThemeToggle } from '../components/ThemeToggle';

const WEEKDAY_LABELS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalSales: 0,
    orderCount: 0,
    topProduct: 'N/A',
    inventoryAlerts: 0,
    salesComparison: 0,
    unpaidValue: 0
  });
  const [weeklySales, setWeeklySales] = useState<{ label: string; value: number; pct: number }[]>([]);

  const [inventory, setInventory] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchInventory();
  }, []);

  const fetchStats = async () => {
    try {
      const todayStart = startOfDay(new Date());
      const yesterdayStart = new Date(todayStart);
      yesterdayStart.setDate(yesterdayStart.getDate() - 1);
      const weekStart = new Date(todayStart);
      weekStart.setDate(weekStart.getDate() - 6);

      const res = await apiClient.get('/orders', {
        params: { startDate: weekStart.toISOString(), endDate: new Date().toISOString() }
      });
      const orders = res.data;

      // Collected revenue only: a pending unpaid order is ordered value, not
      // money in the till. Unpaid value is shown separately.
      const salesOf = (list: any[]) =>
        list.filter(o => o.status !== 'cancelado' && o.paymentStatus === 'collected')
          .reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0);
      const unpaidOf = (list: any[]) =>
        list.filter(o => o.status !== 'cancelado' && o.paymentStatus === 'unpaid')
          .reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0);

      const todayOrders = orders.filter((o: any) => new Date(o.createdAt) >= todayStart);
      const yesterdayOrders = orders.filter((o: any) => {
        const created = new Date(o.createdAt);
        return created >= yesterdayStart && created < todayStart;
      });
      const todayUnpaid = unpaidOf(todayOrders);

      const todaySales = salesOf(todayOrders);
      const yesterdaySales = salesOf(yesterdayOrders);
      const salesComparison = yesterdaySales > 0
        ? Math.round(((todaySales - yesterdaySales) / yesterdaySales) * 100)
        : (todaySales > 0 ? 100 : 0);

      const days: { label: string; value: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const dayStart = new Date(todayStart);
        dayStart.setDate(dayStart.getDate() - i);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        const dayOrders = orders.filter((o: any) => {
          const created = new Date(o.createdAt);
          return created >= dayStart && created < dayEnd;
        });
        days.push({ label: WEEKDAY_LABELS[dayStart.getDay()], value: salesOf(dayOrders) });
      }
      const maxValue = Math.max(...days.map(d => d.value), 1);
      setWeeklySales(days.map(d => ({ ...d, pct: Math.max((d.value / maxValue) * 100, d.value > 0 ? 4 : 0) })));

      setStats(prev => ({
        ...prev,
        totalSales: todaySales,
        orderCount: todayOrders.length,
        salesComparison,
        unpaidValue: todayUnpaid
      }));
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await apiClient.get('/inventory');
      const data = res.data;
      setInventory(data);
      setStats(prev => ({ ...prev, inventoryAlerts: data.filter((i: any) => i.stock < i.minStock).length }));
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, colorClass }: any) => (
    <Card className="p-6 relative overflow-hidden group">
      <>
        <div className={`absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-10 ${colorClass}`} />
        <div className="flex justify-between items-start mb-4">
          <div className={`p-3 rounded-2xl ${colorClass} text-white shadow-lg`}>
            <Icon size={24} />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-sm font-black ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <h3 className="text-slate-500 dark:text-slate-400 font-bold text-sm uppercase tracking-wider">{title}</h3>
        <p className="text-3xl font-black mt-1 dark:text-white">{value}</p>
      </>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark p-6 lg:p-10 transition-colors duration-300">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
        <div className="flex items-center gap-4">
          <BrandLogo className="h-16 w-16" />
          <div>
            <h1 className="text-4xl font-black tracking-tight text-slate-900 dark:text-white">Dashboard</h1>
            <p className="text-slate-500 font-medium">Gestión inteligente de Choripan Nea</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl mr-4">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'overview' ? 'bg-white dark:bg-slate-800 shadow-sm text-primary' : 'text-slate-500'}`}
            >
              <LayoutDashboard size={18} /> Resumen
            </button>
            <button 
              onClick={() => setActiveTab('products')}
              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'products' ? 'bg-white dark:bg-slate-800 shadow-sm text-primary' : 'text-slate-500'}`}
            >
              <UtensilsCrossed size={18} /> Productos
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'inventory' ? 'bg-white dark:bg-slate-800 shadow-sm text-primary' : 'text-slate-500'}`}
            >
              <Boxes size={18} /> Inventario
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'orders' ? 'bg-white dark:bg-slate-800 shadow-sm text-primary' : 'text-slate-500'}`}
            >
              <ClipboardList size={18} /> Pedidos
            </button>
          </div>
          <ThemeToggle />
          <Button variant="outline" size="icon" className="rounded-full">
            <Bell size={20} />
          </Button>
          <ChangePasscode />
          <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-black shadow-lg">
            JD
          </div>
        </div>
      </header>

      {activeTab === 'overview' ? (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatCard
              title="Ventas Cobradas"
              value={`$${stats.totalSales.toLocaleString()}`}
              icon={DollarSign}
              trend={stats.salesComparison}
              colorClass="bg-green-500"
            />
            <StatCard
              title="Por Cobrar"
              value={`$${stats.unpaidValue.toLocaleString()}`}
              icon={ArrowUpRight}
              colorClass="bg-amber-500"
            />
            <StatCard 
              title="Pedidos Hoy" 
              value={stats.orderCount} 
              icon={TrendingUp} 
              colorClass="bg-blue-500"
            />
            <StatCard 
              title="Alertas Inventario" 
              value={stats.inventoryAlerts} 
              icon={Package} 
              colorClass="bg-red-500"
            />
            <StatCard 
              title="Producto Estrella" 
              value="Choripan Nea" 
              icon={BarChart3} 
              colorClass="bg-orange-500"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Inventory Table */}
            <Card className="lg:col-span-2 p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black flex items-center gap-3">
                  <Package className="text-primary" />
                  Inventario
                </h2>
                <Button variant="outline" size="sm">Ver Todo</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-100 dark:border-slate-800">
                      <th className="pb-4 font-black">Insumo</th>
                      <th className="pb-4 font-black">Stock</th>
                      <th className="pb-4 font-black">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-slate-900">
                    {inventory.map((item: any) => (
                      <tr key={item._id} className="text-slate-700 dark:text-slate-300 group hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                        <td className="py-5 font-bold">{item.name}</td>
                        <td className="py-5 font-medium">{item.stock} {item.unit}</td>
                        <td className="py-5">
                          <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                            item.stock < item.minStock 
                              ? 'bg-red-100 text-red-600 dark:bg-red-500/10 dark:text-red-400' 
                              : 'bg-green-100 text-green-600 dark:bg-green-500/10 dark:text-green-400'
                          }`}>
                            {item.stock < item.minStock ? 'Crítico' : 'Óptimo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Sales Comparison Chart */}
            <Card className="p-8 flex flex-col">
              <h2 className="text-2xl font-black mb-8">Ventas Semanales</h2>
              <div className="flex-1 flex items-end justify-around gap-3 min-h-[200px]">
                {weeklySales.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-3 group">
                    <div className="w-full bg-slate-100 dark:bg-slate-900 rounded-2xl relative overflow-hidden h-full flex items-end">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${day.pct}%` }}
                        transition={{ delay: i * 0.1, duration: 0.8, ease: "easeOut" }}
                        className="w-full bg-primary/20 group-hover:bg-primary transition-colors rounded-t-xl relative"
                      >
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          ${day.value.toLocaleString()}
                        </div>
                      </motion.div>
                    </div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                      {day.label}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      ) : activeTab === 'products' ? (
        <ProductManager />
      ) : activeTab === 'inventory' ? (
        <InventoryManager />
      ) : (
        <OrderManager />
      )}
    </div>
  );
};

export default AdminDashboard;
